import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Compass, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import TripForm from "@/components/planner/TripForm";
import EstimateResult from "@/components/planner/EstimateResult";
import { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlanRoute() {
  const { token } = useParams();
  const { lang } = useLang();
  const [ctx, setCtx] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [trip, setTrip] = useState({ toll_preference: "balanced", stops: [], addons: [], pet_count: 1 });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setTrip((t) => ({ ...t, [k]: v }));

  useEffect(() => {
    axios.get(`${BASE}/plan/${token}`)
      .then((res) => {
        setCtx(res.data);
        setTrip((t) => ({ ...t, client_name: res.data.client_name || "", client_email: res.data.client_email || "" }));
      })
      .catch(() => setInvalid(true));
  }, [token]);

  const estimate = async () => {
    if (!trip.start || !trip.end) return toast.error("Please enter a start and destination.");
    setBusy(true);
    try {
      const res = await axios.post(`${BASE}/plan/${token}/estimate`, trip);
      setResult(res.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setBusy(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${BASE}/plan/${token}/submit`, { ...trip, lang });
      setSubmitted(true);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setBusy(false);
  };

  if (invalid)
    return <Centered><h1 className="font-serif text-2xl text-charcoal-800">This planning link is invalid or expired.</h1>
      <p className="text-charcoal-500 mt-2">Please contact Hola Movers for a new link.</p></Centered>;
  if (!ctx) return <Centered><Loader2 className="animate-spin text-terracotta" /></Centered>;

  if (submitted)
    return <Centered>
      <CheckCircle2 className="text-sage" size={48} />
      <h1 className="font-serif text-2xl text-charcoal-800 mt-4">Request received!</h1>
      <p className="text-charcoal-600 mt-2 max-w-md">Thank you — our team will review your route and pricing and follow up shortly. Want to talk it through sooner?</p>
      <a href="/book" data-testid="plan-book-consult" className="mt-5 inline-block rounded-full bg-terracotta px-6 py-2.5 text-sm text-white font-medium hover:bg-terracotta-hover">Request a consultation</a>
    </Centered>;

  return (
    <div className="min-h-screen bg-[#F7F4EF]">
      <header className="bg-white border-b border-sand-200">
        <div className="max-w-3xl mx-auto flex items-center gap-2 px-5 h-16">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-terracotta text-white"><Compass size={18} /></span>
          <span className="font-serif text-lg text-charcoal-800">Hola Movers · Route Planner</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        <h1 className="font-serif text-3xl text-charcoal-800">Plan your relocation route</h1>
        <p className="text-charcoal-600 mt-2 mb-6">{ctx.safety_statement}</p>

        <div className="rounded-2xl bg-white border border-sand-200 p-5 sm:p-6 shadow-sm">
          <TripForm trip={trip} set={set} addons={ctx.addons || []} showClientFields />
          <button data-testid="plan-estimate-btn" onClick={estimate} disabled={busy}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-charcoal-800 px-6 py-3 text-sm text-white font-medium hover:bg-charcoal-900 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} See the safest route & estimate
          </button>
        </div>

        {result && (
          <div className="mt-8">
            <EstimateResult route={result.route} pricing={result.pricing} />
            <div className="mt-6 flex flex-wrap gap-3">
              <button data-testid="plan-submit-btn" onClick={submit} disabled={busy}
                className="rounded-full bg-terracotta px-6 py-3 text-sm text-white font-medium hover:bg-terracotta-hover disabled:opacity-60">
                Submit this for a formal quote
              </button>
              <a href="/book" className="rounded-full border border-sand-300 px-6 py-3 text-sm text-charcoal-700 font-medium hover:bg-sand-50">
                Request a consultation
              </a>
            </div>
            <p className="text-xs text-charcoal-400 mt-3">This is an estimate. A coordinator will confirm the final route and price before anything is booked.</p>
          </div>
        )}
      </main>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center justify-center text-center px-6">{children}</div>
  );
}
